import React, { useCallback, useEffect, useState } from "react";
import api from "@/lib/api";
import { Card, Button, Input, Select, Field, Stat, Textarea } from "@/components/UI";
import { Plus, Robot, Trash } from "@phosphor-icons/react";
import { toast } from "sonner";


export function LelyConsumptionTab() {
  const [stockItems, setStockItems] = useState([]);
  const [entries, setEntries] = useState([]);
  const [showForm, setShowForm] = useState(false);
  const [saving, setSaving] = useState(false);
  const [selectedStock, setSelectedStock] = useState("");

  const thisMonday = React.useMemo(() => {
    const d = new Date();
    const day = d.getDay() || 7;
    d.setDate(d.getDate() - (day - 1));
    return d.toISOString().slice(0, 10);
  }, []);
  const [form, setForm] = useState({ week_start_date: thisMonday, amount: "", unit: "kg", notes: "" });

  const load = useCallback(() => {
    api.get("/stock").then(r => setStockItems((r.data || []).filter(s =>
      ["kg", "ton", "gr", "gram"].includes((s.unit || "").toLowerCase())
    ))).catch(() => {});
    const q = selectedStock ? `?stock_id=${selectedStock}` : "";
    api.get(`/stock/lely-consumption${q}`).then(r => setEntries(r.data)).catch(() => {});
  }, [selectedStock]);
  useEffect(() => { load(); }, [load]);

  const submit = async (e) => {
    e.preventDefault();
    if (!selectedStock) return toast.error("Yem kalemini seçin");
    if (!form.amount || Number(form.amount) <= 0) return toast.error("Geçerli bir miktar girin");
    setSaving(true);
    try {
      const { data } = await api.post("/stock/lely-consumption", {
        stock_id: selectedStock,
        week_start_date: form.week_start_date,
        amount: Number(form.amount),
        unit: form.unit,
        notes: form.notes || null,
      });
      toast.success(`Lely tüketimi kaydedildi (${data.deducted_kg} kg, ${data.cost} ₺)`);
      if (data.warning) toast.warning(data.warning);
      setForm({ week_start_date: thisMonday, amount: "", unit: "kg", notes: "" });
      setShowForm(false);
      load();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Hata");
    } finally {
      setSaving(false);
    }
  };

  const del = async (id) => {
    if (!window.confirm("Bu Lely tüketim kaydını silmek istediğinize emin misiniz? Stok geri iade edilecek.")) return;
    try {
      await api.delete(`/stock/lely-consumption/${id}`);
      toast.success("Kayıt silindi, stok geri iade edildi");
      load();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Silme başarısız");
    }
  };

  const fmt = (v) => Number(v || 0).toLocaleString("tr-TR");
  const totalWeekKg = entries.reduce((s, e) => s + (e.deducted_kg || 0), 0);
  const totalWeekCost = entries.reduce((s, e) => s + (e.cost || 0), 0);

  return (
    <div data-testid="lely-tab">
      <Card className="mb-4" testId="lely-intro">
        <div className="flex items-start gap-3">
          <div className="p-2 bg-[#E4EEE6] rounded-md">
            <Robot size={22} weight="duotone" className="text-[#2D4A36]" />
          </div>
          <div className="flex-1">
            <h3 className="heading text-lg mb-1">Lely Robot — Haftalık Tüketim Girişi</h3>
            <p className="text-sm text-[#5e675e]">
              Lely robotun bir hafta boyunca ineklere dağıttığı toplam yem miktarını buraya girin.
              Sistem bu miktarı <b>kg-normalize</b> olarak <b>merkezi stoktan otomatik düşer</b>, finans giderine yazar ve günlük ortalama tüketim hesabına dahil eder.
            </p>
          </div>
        </div>
      </Card>

      <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-4" data-testid="lely-kpis">
        <Stat testId="lely-total-entries" label="Toplam Kayıt" value={entries.length} tone="accent" />
        <Stat testId="lely-total-kg" label="Toplam Tüketim" value={fmt(totalWeekKg)} unit="kg" tone="ok" />
        <Stat testId="lely-total-cost" label="Toplam Maliyet" value={fmt(Math.round(totalWeekCost))} unit="₺" tone="warn" />
        <div className="border border-[#E5E2DC] rounded-md p-3 bg-white">
          <div className="text-xs uppercase tracking-wider text-[#8a938c]">Filtre</div>
          <Select value={selectedStock} onChange={e => setSelectedStock(e.target.value)} data-testid="lely-filter-stock" className="mt-1">
            <option value="">Tüm yem kalemleri</option>
            {stockItems.map(s => <option key={s.id} value={s.id}>{s.name}</option>)}
          </Select>
        </div>
      </div>

      <div className="mb-3">
        <Button data-testid="lely-add-btn" onClick={() => setShowForm(v => !v)}>
          <Plus size={14} className="inline mr-1" /> {showForm ? "Kapat" : "Yeni Haftalık Kayıt"}
        </Button>
      </div>

      {showForm && (
        <Card className="mb-4" testId="lely-form-card">
          <form onSubmit={submit} className="grid md:grid-cols-4 gap-3">
            <Field label="Yem Kalemi *">
              <Select required data-testid="lely-form-stock" value={selectedStock} onChange={e => setSelectedStock(e.target.value)}>
                <option value="">Seçin...</option>
                {stockItems.map(s => (
                  <option key={s.id} value={s.id}>{s.name} ({s.quantity} {s.unit} stok)</option>
                ))}
              </Select>
            </Field>
            <Field label="Hafta Başlangıcı *">
              <Input type="date" required data-testid="lely-form-week" value={form.week_start_date}
                     onChange={e => setForm({ ...form, week_start_date: e.target.value })} />
            </Field>
            <Field label="Miktar *">
              <Input type="number" step="0.01" min="0" required data-testid="lely-form-amount"
                     value={form.amount}
                     onChange={e => setForm({ ...form, amount: e.target.value })}
                     placeholder="Örn: 350" />
            </Field>
            <Field label="Birim">
              <Select data-testid="lely-form-unit" value={form.unit} onChange={e => setForm({ ...form, unit: e.target.value })}>
                <option value="kg">kg</option>
                <option value="ton">ton</option>
                <option value="gr">gram</option>
              </Select>
            </Field>
            <div className="md:col-span-4">
              <Field label="Notlar (opsiyonel)">
                <Textarea rows={1} value={form.notes} onChange={e => setForm({ ...form, notes: e.target.value })}
                          placeholder="Örn: Konsantre yem robot hattı" />
              </Field>
            </div>
            <div className="md:col-span-4 flex gap-2 justify-end">
              <Button type="button" variant="secondary" onClick={() => setShowForm(false)}>İptal</Button>
              <Button type="submit" disabled={saving} data-testid="lely-form-submit">
                {saving ? "Kaydediliyor..." : "Kaydet ve Stoktan Düş"}
              </Button>
            </div>
          </form>
        </Card>
      )}

      <Card className="!p-0 overflow-hidden">
        <div className="overflow-x-auto">
          <table className="data-table" data-testid="lely-table">
            <thead>
              <tr>
                <th>Hafta</th>
                <th>Yem Kalemi</th>
                <th className="text-right">Girilen Miktar</th>
                <th className="text-right">Düşülen (kg)</th>
                <th className="text-right">Maliyet</th>
                <th>Notlar</th>
                <th className="text-right whitespace-nowrap">İşlem</th>
              </tr>
            </thead>
            <tbody>
              {entries.map(e => (
                <tr key={e.id} data-testid={`lely-row-${e.id}`}>
                  <td className="tabnum">{e.week_start_date}</td>
                  <td className="font-semibold">
                    <Robot size={14} weight="duotone" className="inline mr-1.5 text-[#2D4A36]" />
                    {e.stock_name}
                  </td>
                  <td className="text-right tabnum">{fmt(e.amount)} {e.unit}</td>
                  <td className="text-right tabnum font-semibold text-[#8B6914]">{fmt(e.deducted_kg)} kg</td>
                  <td className="text-right tabnum text-[#C45B45] font-semibold">{fmt(e.cost)} ₺</td>
                  <td className="text-xs text-[#6b746c]">{e.notes || "—"}</td>
                  <td className="text-right whitespace-nowrap">
                    <button onClick={() => del(e.id)}
                            className="inline-flex items-center gap-1 px-2 py-1 rounded-md text-xs font-medium bg-[#FBECEA] text-[#C45B45] hover:bg-[#f5d5ce] border border-[#f2d4cb]"
                            title="Kaydı sil, stoku iade et"
                            data-testid={`lely-del-${e.id}`}>
                      <Trash size={14} weight="bold" /> Sil
                    </button>
                  </td>
                </tr>
              ))}
              {!entries.length && (
                <tr><td colSpan={7} className="text-center py-10 text-[#8a938c]">
                  <Robot size={32} weight="duotone" className="mx-auto mb-2 opacity-40" />
                  Henüz Lely tüketim kaydı yok. "Yeni Haftalık Kayıt" ile başlayın.
                </td></tr>
              )}
            </tbody>
          </table>
        </div>
      </Card>
    </div>
  );
}
